import { DATE_FORMAT, type ProjectCode, type CountryCode, Platform } from '@constants'
import { getLocale } from '@cosign/utils/cookie'
import { useDialog } from '@dni-hooks/useDialog'
import { Dialog, DialogActions, DialogContent } from '@fortune-ui'
import { mutateAllECommerceOrders } from '@hooks-api/ec/useApiECommerceOrders'
import { useApiECommerceUpdateOrder } from '@hooks-api/ec/useApiECommerceUpdateOrder'
import { useApiBlockoutDeliveryDays } from '@hooks-api/fortune/use-api-blockout-delivery-days'
import { type ServiceOrderItem } from '@hooks-api/types/dataModel/eCommerceOrders'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { HStack } from '@pets-ui/HStack/HStack'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { getCountryCodeFromPathname, getDeliveryDateRange } from '@utils'
import { add, addDays, differenceInDays, format, fromUnixTime, type Locale } from 'date-fns'
import { enUS, zhTW } from 'date-fns/locale'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { REVISE_DElIVERY_DATE_DIALOG_KEY, SERVER_ERROR_DIALOG_KEY } from '../constants/dialogKey'
import { useContextWpMyAccount } from '../useContextWpMyAccount'

export interface DialogReviseDateProps {
  serviceOrderItems: ServiceOrderItem[]
  remainDeliveryAt: string[]
  remainDeliveryIndex: number
  fortuneOrderId: string
  periodValue: number
}
interface DialogReviseDateComponentProps extends DialogReviseDateProps {
  onClose: () => void
}

const adapterLocaleMap: Record<string, Locale> = {
  zh_TW: zhTW,
  en_US: enUS,
  zh_HK: zhTW,
}

const BUFFER_DAYS = 2
const SECOND_TO_MILLISECOND = 1000
const BUFFER_DAYS_IN_SECOND = Math.floor(addDays(new Date(), BUFFER_DAYS).getTime() / SECOND_TO_MILLISECOND)
const WAREHOUSE_MAX_DAYS = 365 * 5

const DialogReviseDateComponent = ({ onClose, remainDeliveryAt, serviceOrderItems, remainDeliveryIndex, fortuneOrderId, periodValue }: DialogReviseDateComponentProps) => {
  const { openDialog: openServerErrorDialog } = useDialog(SERVER_ERROR_DIALOG_KEY)
  const { t } = useTranslation()
  const { projectCode } = useContextWpMyAccount()
  const countryCode = getCountryCodeFromPathname(window.location.pathname, 'TW') as CountryCode
  const { trigger, isMutating } = useApiECommerceUpdateOrder()
  const { destructuredData: blockoutDeliveryDays } = useApiBlockoutDeliveryDays({ project_code: projectCode as ProjectCode, country_code: countryCode, platform: Platform.WORDPRESS })
  const [date, setDate] = useState(remainDeliveryAt[remainDeliveryIndex])
  const wordPressLocale = getLocale()
  const adapterLocale = adapterLocaleMap[wordPressLocale] ?? zhTW
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()

  const defaultDate = fromUnixTime(blockoutDeliveryDays?.latest_delivery_date ?? BUFFER_DAYS_IN_SECOND)
  const excludeDays = useMemo(() => {
    const weekdays: number[] = []
    if (blockoutDeliveryDays?.is_sat_on === false && !weekdays.includes(6)) weekdays.push(6)
    if (blockoutDeliveryDays?.is_sun_on === false && !weekdays.includes(0)) weekdays.push(0)
    return weekdays
  }, [blockoutDeliveryDays?.is_sat_on, blockoutDeliveryDays?.is_sun_on])
  const { minDate, maxDate, filterDate } = useMemo(() => {
    return getDeliveryDateRange({
      startDate: defaultDate,
      maxSelectableDeliveryDays: WAREHOUSE_MAX_DAYS,
      blockoutDeliveryDays: blockoutDeliveryDays?.blockout_delivery_days,
      excludeDays,
    })
  }, [defaultDate, blockoutDeliveryDays, excludeDays])

  useEffect(() => {
    const validateDate = () => {
      if (date && minDate && new Date(date) < minDate) {
        setDate(minDate.toISOString())
      }
      if (date && maxDate && new Date(date) > maxDate) {
        setDate(maxDate.toISOString())
      }
    }
    validateDate()
  }, [date, minDate, maxDate])

  const handleClickSubmit = async () => {
    if (!date || !remainDeliveryAt[remainDeliveryIndex]) return
    const newRemainDeliveryAt: string[] = []
    const diffDays = differenceInDays(new Date(date), new Date(remainDeliveryAt[remainDeliveryIndex]))
    const isPutOff = diffDays > 0

    for (let i = 0; i < remainDeliveryAt.length; i++) {
      const deliveryDate = remainDeliveryAt[i] || ''
      const isSelectedDate = i === remainDeliveryIndex
      const isDelivered = i < remainDeliveryIndex
      if (isSelectedDate) {
        newRemainDeliveryAt.push(new Date(date).toISOString())
      }
      else if (isDelivered) {
        newRemainDeliveryAt.push(new Date(deliveryDate).toISOString())
      }
      else {
        const baseDate = i === remainDeliveryIndex + 1
          ? new Date(date)
          : new Date(newRemainDeliveryAt[i - 1] || '')

        let nextDeliveryDate = add(baseDate, { months: periodValue })
        while (
          blockoutDeliveryDays?.blockout_delivery_days?.includes(format(nextDeliveryDate, DATE_FORMAT))
          || excludeDays.includes(nextDeliveryDate.getDay())
        ) {
          nextDeliveryDate = add(nextDeliveryDate, { days: isPutOff ? 1 : -1 })
        }
        newRemainDeliveryAt.push(nextDeliveryDate.toISOString())
      }
    }

    try {
      await trigger({
        id: fortuneOrderId,
        service_order_items: [{
          // @ts-expect-error - TODO: fix this
          product_id: serviceOrderItems[0].product_id,
          // @ts-expect-error - TODO: fix this
          quantity_per_delivery: serviceOrderItems[0].quantity_per_delivery,
        }],
        remain_delivery_at: newRemainDeliveryAt,
      })
      openSnackbar({
        message: '修改日期成功',
        severity: 'success',
      })
      onClose()
    }
    catch (e) {
      console.error('useApiECommerceUpdateOrder error', e)
      openSnackbar({
        message: '修改日期失敗',
        severity: 'error',
      })
      onClose()
      openServerErrorDialog()
    }
    finally {
      mutateAllECommerceOrders?.()
    }
  }

  const isUnavailableDate = useMemo(() => {
    if (!date) return false
    return excludeDays.includes(new Date(date).getDay())
  }, [date, excludeDays])

  return (
    <Box p={4}>
      <Text align="center" variant="h4">
        {t('wpOrder.reviseDate')}
      </Text>
      <DialogContent sx={{ p: 0, m: '8px 0 16px 0', overflow: 'hidden' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
          <DatePicker
            onChange={e => e && setDate(e.toISOString())}
            // @ts-expect-error - TODO: fix this
            value={new Date(date)}
            format="yyyy-MM-dd"
            shouldDisableDate={date => !filterDate(date)}
            minDate={minDate}
            maxDate={maxDate}
            sx={{
              'width': '100%',
              '& .MuiInputBase-root': {
                height: 36,
                lineHeight: 1.4,
              },
            }}
          />
        </LocalizationProvider>
        {isUnavailableDate && (
          <Text color={colors.red600}>
            {t('wpMyAccount.dateUnavaialbe', { defaultValue: '該日期無法配送，請重新選擇日期' })}
          </Text>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 0 }}>
        <HStack gap={2} flexWrap="nowrap" width="100%">
          <Button loading={isMutating} fullWidth variant="outlined" onClick={onClose}>{t('wpOrder.cancel')}</Button>
          <Button loading={isMutating} fullWidth variant="filled" disabled={isUnavailableDate} onClick={handleClickSubmit}>{t('wpOrder.submit')}</Button>
        </HStack>
      </DialogActions>
    </Box>
  )
}

export function DialogReviseDate() {
  const { open, closeDialog, ...rest } = useDialog<DialogReviseDateProps>(REVISE_DElIVERY_DATE_DIALOG_KEY)

  return (
    <Dialog
      maxWidth="xs"
      open={open}
      onClose={closeDialog}
      disablePortal
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '320px',
          borderRadius: '8px',
        },
      }}
    >
      <DialogReviseDateComponent
        onClose={closeDialog}
        {...rest}
      />
    </Dialog>
  )
}
